import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import * as Icons from 'lucide-react';
import NotificationSystem from './NotificationSystem';
import AlertBadge from './AlertBadge';

const RoleNavigation = ({ setSidebarOpen, onLogout }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Roles soportando multi-rol (user.role + user.roles[] del perfil)
  const rolesFromProfile = Array.isArray(user?.roles) ? user.roles.map(r => r.role_name) : [];
  const roleNames = Array.from(new Set([user?.role, ...rolesFromProfile].filter(Boolean)));

  // Definir qué vistas puede ver cada rol
  const allViews = [
    {
      id: 'admin-dashboard',
      name: 'Tablero Ejecutivo',
      icon: Icons.BarChart,
      path: '/admin/dashboard',
      description: 'Panel de alta gerencia, patrimonio y liquidez',
      roles: ['admin', 'gerente']
    },
    {
      id: 'dashboard',
      name: 'Dashboard',
      icon: Icons.LayoutDashboard,
      path: '/dashboard',
      description: 'Vista general operativa',
      roles: ['logistica', 'cartera', 'facturador']
    },
    {
      id: 'todos-pedidos',
      name: 'Todos los Pedidos',
      icon: Icons.List,
      path: '/orders?view=todos',
      description: 'Ver todos los pedidos con todos los estados',
      clearFilters: true,
      roles: ['admin', 'facturador', 'cartera', 'logistica']
    },
    {
      id: 'facturacion-group',
      name: 'Facturación',
      icon: Icons.FileText,
      roles: ['admin', 'facturador'],
      children: [
        {
          id: 'facturacion',
          name: 'Pedidos por Facturar',
          icon: Icons.ShoppingCart,
          path: '/orders?view=facturacion&status=pendiente_por_facturacion',
          description: 'Gestión de pedidos pendientes de factura'
        },
        {
          id: 'historial_facturacion',
          name: 'Cierre e Historial',
          icon: Icons.History,
          path: '/orders?view=historial_facturacion',
          description: 'Consulta de ventas y auditoría diaria'
        },
        {
          id: 'clientes',
          name: 'Gestión de Clientes',
          icon: Icons.Users,
          path: '/customers',
          description: 'Administración y sincronización de clientes'
        },
        {
          id: 'anniversary-campaign',
          name: 'Campaña Aniversario',
          icon: Icons.Gift,
          path: '/anniversary-campaign',
          description: 'Bonos y tarjetas de fidelización'
        },
        {
          id: 'inventory-billing',
          name: 'Punto de Venta (POS)',
          icon: Icons.Zap,
          path: '/inventory-billing',
          description: 'Facturación rápida de inventario'
        },
        {
          id: 'siigo-invoices',
          name: 'Facturas SIIGO',
          icon: Icons.FileText,
          path: '/siigo-invoices',
          description: 'Consulta de facturas en el ERP'
        },
        {
          id: 'quotations',
          name: 'Cotizaciones',
          icon: Icons.Calculator,
          path: '/quotations',
          description: 'Generación de cotizaciones'
        }
      ]
    },
    {
      id: 'cartera-group',
      name: 'Cartera',
      icon: Icons.CreditCard,
      roles: ['admin', 'cartera'],
      children: [
        {
          id: 'cartera',
          name: 'Pedidos en Cartera',
          icon: Icons.ListChecks,
          path: '/orders?view=cartera',
          description: 'Verificación de pagos'
        },

        {
          id: 'route-settlement',
          name: 'Liquidación de Rutas',
          icon: Icons.FileCheck,
          path: '/cartera/settle-routes',
          description: 'Cierre financiero y aprobación de gastos de rutas'
        },
        {
          id: 'pending-rcs',
          name: 'RCs Pendientes',
          icon: Icons.FileWarning,
          path: '/cartera/pending-rcs',
          description: 'Pedidos con RCs faltantes por crear en Siigo'
        },
        {
          id: 'treasury-audit',
          name: 'Auditoría Cartera',
          icon: Icons.ShieldCheck,
          path: '/treasury-audit',
          description: 'Historial de consignaciones y cambios de base'
        },
        {
          id: 'overpayments',
          name: 'Sobrepagos',
          icon: Icons.AlertCircle,
          path: '/overpayments',
          description: 'Devoluciones pendientes por sobrepago'
        },
        {
          id: 'messenger-tracking',
          name: 'Seguimiento Mensajeros',
          icon: Icons.MapPin,
          path: '/messenger-tracking',
          description: 'Mapa de paradas y resumen del día',
          roles: ['admin', 'cartera']
        },
        {
          id: 'live-tracking',
          name: 'Rastreo GPS en Vivo',
          icon: Icons.Navigation,
          path: '/live-tracking',
          roles: ['admin', 'cartera']
        },
        {
          id: 'cash-box',
          name: 'Gestión de Caja',
          icon: Icons.Wallet,
          path: '/cash-box',
          description: 'Control de efectivo y egresos'
        }
      ]
    },
    {
      id: 'logistica-group',
      name: 'Logística',
      icon: Icons.Package,
      roles: ['admin', 'logistica', 'empaque', 'empacador', 'cartera', 'facturador'],
      children: [
        {
          id: 'logistica',
          name: 'Gestión Logística',
          icon: Icons.Boxes,
          path: '/orders?view=logistica&status=en_logistica',
          description: 'Preparación y asignación de pedidos'
        },
        {
          id: 'empaque',
          name: 'Empaque',
          icon: Icons.Box,
          path: '/packaging',
          description: 'Control de calidad y empaque'
        },
        {
          id: 'ready-to-deliver',
          name: 'Pedidos por Entregar',
          icon: Icons.ClipboardCheck,
          path: '/ready-to-deliver',
          description: 'Listado de despachos listos'
        },
        // Consolidado en una sola pantalla: Despacho de Ruta o Domicilio
        // {
        //   id: 'logistics-handoff',
        //   name: 'Handoff Físico',
        //   icon: Icons.PackageCheck,
        //   path: '/logistics/handoff',
        //   description: 'Confirmación de salida a mensajería'
        // },
        {
          id: 'route-dispatch',
          name: 'Despacho de Ruta o Domicilio',
          icon: Icons.Navigation,
          path: '/route-dispatch',
          description: 'Asignar mensajeros y confirmar handoff'
        },
        {
          id: 'packaging-progress',
          name: 'Progreso Empaque',
          icon: Icons.Activity,
          path: '/packaging-progress',
          description: 'Seguimiento en tiempo real'
        },
        {
          id: 'evidence-gallery',
          name: 'Galería Evidencias',
          icon: Icons.Image,
          path: '/packaging/evidence-gallery',
          description: 'Fotos de empaque por pedido'
        },
        {
          id: 'live-tracking-logistics',
          name: 'Rastreo GPS en Vivo',
          icon: Icons.Navigation,
          path: '/live-tracking',
          roles: ['admin', 'logistica']
        }
      ]
    },
    {
      id: 'mensajero-group',
      name: 'Mensajero',
      icon: Icons.Truck,
      roles: ['admin', 'logistica', 'mensajero'],
      children: [
        {
          id: 'my-route',
          name: 'Mi Ruta de Hoy',
          icon: Icons.Navigation,
          path: '/my-route',
          description: 'Ver ruta optimizada del día'
        }
      ]
    },
    {
      id: 'kiosk-attendance-public',
      name: 'Asistencia',
      icon: Icons.Camera,
      path: '/attendance',
      description: 'Registro de asistencia para personal',
      roles: ['gerente', 'cartera', 'logistica', 'facturador', 'empaque', 'empacador']
    },
    {
      id: 'marketing-group',
      name: 'Marketing',
      icon: Icons.TrendingUp,
      roles: ['admin', 'gerente'],
      children: [
        {
          id: 'marketing-analytics',
          name: 'Analytics Social',
          icon: Icons.BarChart2,
          path: '/marketing-analytics',
          description: 'Correlación de ventas y redes sociales'
        }
      ]
    },
    {
      id: 'special-products-group',
      name: 'Productos Especiales',
      icon: Icons.Star,
      roles: ['admin', 'cartera', 'facturador', 'logistica'],
      children: [
        {
          id: 'special-inventory',
          name: 'Inventario Especial',
          icon: Icons.Box,
          path: '/admin/inventory/special',
          description: 'Gestión de productos locales',
          roles: ['admin', 'logistica']
        },
        {
          id: 'internal-pos',
          name: 'Venta POS Interno',
          icon: Icons.Zap,
          path: '/admin/pos/internal',
          description: 'Crear ventas de productos especiales'
        },
        {
          id: 'internal-history',
          name: 'Historial Pedidos POS',
          icon: Icons.History,
          path: '/admin/orders/internal-history',
          description: 'Ver y vincular pedidos internos'
        }
      ]
    }
  ];

  // Filtrar vistas según el rol del usuario
  const roleViews = allViews
    .filter(view => view.roles.some(r => roleNames.includes(r)))
    .map(view => {
      if (view.children) {
        return {
          ...view,
          children: view.children.filter(child => {
            // Si el hijo tiene roles definidos, filtrar por ellos
            if (child.roles) {
              return child.roles.some(r => roleNames.includes(r));
            }
            return true;
          })
        };
      }
      return view;
    });

  const scrollContainerRef = React.useRef(null);
  const dropdownRef = React.useRef(null);
  const [showLeftArrow, setShowLeftArrow] = React.useState(false);
  const [showRightArrow, setShowRightArrow] = React.useState(false);
  const [activeDropdown, setActiveDropdown] = React.useState(null);
  const [dropdownCoords, setDropdownCoords] = React.useState({ left: 0, top: 0 });

  const checkScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  // Cerrar dropdown al hacer clic fuera o al hacer scroll
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        // Verificar si el clic fue en el botón disparador (para evitar doble toggle)
        if (!event.target.closest('.dropdown-trigger')) {
          setActiveDropdown(null);
        }
      }
    };

    const handleScroll = () => setActiveDropdown(null);

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true); // true para capturar scroll en cualquier contenedor

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [activeDropdown]);

  React.useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [roleViews]);

  const getCurrentView = () => {
    const path = location.pathname;
    const search = location.search;
    const urlParams = new URLSearchParams(search);

    if (path === '/dashboard') return 'dashboard';

    const viewParam = urlParams.get('view');
    if (viewParam) {
      if (viewParam === 'todos') return 'todos-pedidos';
      return viewParam;
    }

    const statusParam = urlParams.get('status');
    if (path === '/orders' && statusParam) {
      if (statusParam === 'revision_cartera' || statusParam === 'pendiente_pago') {
        return 'cartera';
      }
      if (statusParam === 'en_logistica' || statusParam === 'preparando' || statusParam === 'listo_envio') {
        return 'logistica';
      }
      if (statusParam === 'en_reparto' || statusParam === 'entregado' || statusParam === 'devuelto') {
        return 'mensajero';
      }
    }

    if (path === '/users') return 'usuarios';
    if (path === '/postventa') return 'postventa';
    if (path === '/postventa-analytics') return 'postventa-analytics';
    if (path === '/automation') return 'automation';
    if (path === '/orders') return 'facturacion';
    if (path === '/billing' || path === '/siigo-invoices') return 'facturacion';
    if (path === '/siigo-consulta') return 'siigo-consulta';
    if (path === '/ready-to-deliver') return 'ready-to-deliver';
    if (path === '/logistics/handoff') return 'logistics-handoff';
    if (path === '/route-dispatch') return 'route-dispatch';
    if (path === '/packaging') return 'empaque';
    if (path === '/packaging-progress') return 'packaging-progress';
    if (path === '/packaging/evidence-gallery') return 'evidence-gallery';
    if (path === '/products') return 'productos';
    if (path === '/delivery-methods') return 'delivery-methods';
    if (path === '/anniversary-campaign') return 'anniversary-campaign';
    if (path === '/marketing-analytics') return 'marketing-analytics';
    if (path === '/quotations') return 'cotizaciones';
    if (path === '/customers') return 'clientes';
    if (path === '/route-collections') return 'route-collections';
    if (path === '/cartera/settle-routes') return 'route-settlement';
    if (path === '/cartera/validate-returns') return 'validate-returns';
    if (path === '/cartera/pending-rcs') return 'pending-rcs';
    if (path === '/messenger-tracking') return 'messenger-tracking';
    if (path === '/live-tracking') return 'live-tracking';
    if (path === '/cash-box') return 'cash-box';

    if (path === '/treasury-audit') return 'treasury-audit';
    if (path === '/financial-closure') return 'financial-closure';
    if (path === '/inventory-management') return 'inventory-management';
    if (path === '/admin/inventory/special') return 'special-inventory';
    if (path === '/admin/pos/internal') return 'internal-pos';
    if (path === '/admin/orders/internal-history') return 'internal-history';

    return 'dashboard';
  };

  const currentView = getCurrentView();

  const handleRoleChange = (roleView) => {
    setActiveDropdown(null);
    navigate(roleView.path);
  };

  const handleLogoutClick = () => {
    if (typeof onLogout === 'function') {
      onLogout();
    } else {
      logout();
    }
  };

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 200;
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // IMPORTANT: Return null check AFTER hooks
  if (roleViews.length === 0) {
    return null;
  }

  return (
    <div className="bg-gray-800 text-white fixed top-0 left-0 md:left-64 right-0 z-[60] shadow-md border-b border-gray-700 h-12">
      {/* For handle cases where sidebar is hidden (e.g. POS view) */}
      <style dangerouslySetInnerHTML={{
        __html: `
        .md\\:pl-64 + div > .fixed.top-0 { left: 16rem; }
        .min-w-0:not(.md\\:pl-64) > .fixed.top-0 { left: 0; }
      `}} />

      <div className="px-2 sm:px-4 lg:px-8">
        <div className="flex items-center justify-between h-12 relative">

          {/* Lado Izquierdo: Menu Hamburguesa (Móvil) */}
          <button
            type="button"
            className="md:hidden -ml-1 mr-2 h-10 w-10 inline-flex items-center justify-center rounded-md text-gray-400 hover:text-white focus:outline-none"
            onClick={() => setSidebarOpen && setSidebarOpen(true)}
          >
            <Icons.Menu className="h-5 w-5" />
          </button>

          {/* Left Arrow */}
          {showLeftArrow && (
            <button
              onClick={() => scroll('left')}
              className="absolute left-0 z-30 h-full px-2 bg-gradient-to-r from-gray-800 to-transparent flex items-center text-white hover:text-blue-400"
            >
              <Icons.ChevronLeft className="w-5 h-5" />
            </button>
          )}

          <div
            ref={scrollContainerRef}
            onScroll={checkScroll}
            className="flex items-center space-x-1 overflow-x-auto whitespace-nowrap scrollbar-hide flex-1 pr-2 px-6"
          >
            {roleViews.map((roleView) => {
              const hasChildren = roleView.children && roleView.children.length > 0;
              const isGroupActive = hasChildren && roleView.children.some(c => currentView === c.id);
              const isActive = (currentView === roleView.id) || isGroupActive;
              const Icon = roleView.icon;

              if (hasChildren) {
                return (
                  <div key={roleView.id} className="relative">
                    <button
                      onClick={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setDropdownCoords({ left: rect.left, top: rect.bottom + 4 });
                        setActiveDropdown(activeDropdown === roleView.id ? null : roleView.id);
                      }}
                      className={`
                        dropdown-trigger flex items-center rounded-md font-medium transition-colors px-2 py-1 text-xs sm:px-3 sm:py-2 sm:text-sm
                        ${isActive
                          ? 'bg-gray-700 text-white'
                          : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                        }
                      `}
                    >
                      <Icon className="w-4 h-4 mr-2" />
                      {roleView.name}
                      <Icons.ChevronDown className={`w-3 h-3 ml-1 transition-transform ${activeDropdown === roleView.id ? 'rotate-180' : ''}`} />
                    </button>
                  </div>
                );
              }

              return (
                <button
                  key={roleView.id}
                  onClick={() => handleRoleChange(roleView)}
                  className={`
                    flex items-center rounded-md font-medium transition-colors px-2 py-1 text-xs sm:px-3 sm:py-2 sm:text-sm
                    ${isActive
                      ? 'bg-gray-700 text-white'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    }
                  `}
                  title={roleView.description}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {roleView.name}
                </button>
              );
            })}
          </div>

          {/* Right Arrow */}
          {showRightArrow && (
            <button
              onClick={() => scroll('right')}
              className="absolute right-0 md:right-auto md:left-[calc(100%-200px)] lg:right-auto lg:left-auto z-30 h-full px-2 bg-gradient-to-l from-gray-800 to-transparent flex items-center text-white hover:text-blue-400"
              style={{ right: '0' }}
            >
              <Icons.ChevronRight className="w-5 h-5" />
            </button>
          )}

          <div className="flex items-center space-x-2 sm:space-x-4 ml-auto pl-2 border-l border-gray-700 h-10">
            {/* Sistema de Notificaciones */}
            <NotificationSystem />

            {/* Alert Badge - Solo para admins */}
            {user?.role === 'admin' && <AlertBadge />}

            {/* Botón Salir */}
            <button
              onClick={handleLogoutClick}
              className="flex items-center p-1.5 text-gray-400 hover:text-red-400 focus:outline-none transition-colors"
              title="Cerrar Sesión"
            >
              <Icons.LogOut className="h-4 w-4" />
              <span className="hidden sm:block ml-1 text-[10px] font-medium uppercase tracking-wider">Salir</span>
            </button>
          </div>
        </div>
      </div>
      {activeDropdown && (
        <div
          ref={dropdownRef}
          className="fixed bg-gray-800 border border-gray-700 rounded-md shadow-2xl z-[999] overflow-hidden py-1 min-w-[220px]"
          style={{
            left: Math.min(dropdownCoords.left, window.innerWidth - 230),
            top: dropdownCoords.top
          }}
        >
          {roleViews.find(v => v.id === activeDropdown)?.children?.map(child => {
            const ChildIcon = child.icon;
            const isChildActive = currentView === child.id;
            return (
              <button
                key={child.id}
                onClick={() => {
                  handleRoleChange(child);
                  setActiveDropdown(null);
                }}
                className={`
                  flex items-center w-full px-4 py-3 text-xs sm:text-sm text-left transition-colors
                  ${isChildActive
                    ? 'bg-blue-600 text-white font-semibold'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                  }
                `}
              >
                {ChildIcon && <ChildIcon className="w-4 h-4 mr-3 opacity-80" />}
                <div className="flex flex-col">
                  <span>{child.name}</span>
                  {child.description && (
                    <span className="text-[10px] opacity-60 font-normal leading-tight">{child.description}</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RoleNavigation;
